import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDateTime, statusBadgeClass, statusLabel } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  Banknote,
  ArrowLeftRight,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  Wallet,
  Calendar,
  ArrowDownLeft,
  ArrowUpRight,
  RefreshCw,
} from 'lucide-react';

function AccountCard({ account, onClick }) {
  return (
    <div
      onClick={() => onClick(account)}
      className="stat-card cursor-pointer transition-all hover:-translate-y-0.5 hover:border-surface-300 hover:shadow-card-hover dark:hover:border-surface-700"
    >
      <div className="stat-icon stat-icon-brand">
        <Wallet className="h-6 w-6" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="truncate text-xs font-medium uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">{account.name}</p>
        <p className="mt-1.5 text-xl font-medium tracking-tight text-gray-900 tabular-nums dark:text-white">{formatCurrency(account.balance)}</p>
        <p className="text-xs text-gray-400 mt-1">{account.account_number || account.code || '-'}</p>
      </div>
    </div>
  );
}

function TransactionRow({ transaction }) {
  const isIncoming = transaction.type === 'in' || transaction.type === 'transfer_in';
  return (
    <tr>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDateTime(transaction.date)}</span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          {isIncoming ? (
            <ArrowDownLeft className="h-4 w-4 text-success-500" />
          ) : (
            <ArrowUpRight className="h-4 w-4 text-danger-500" />
          )}
          <span className={`badge ${statusBadgeClass(transaction.type)}`}>
            {statusLabel(transaction.type)}
          </span>
        </div>
      </td>
      <td>
        <span className="text-sm text-gray-900 dark:text-white">{transaction.account_name || transaction.account?.name || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200 truncate max-w-xs">{transaction.description || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-500 dark:text-gray-400">{transaction.reference || '-'}</span>
      </td>
      <td className="text-right">
        <span className={`text-sm font-medium ${isIncoming ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
          {isIncoming ? '+' : '-'} {formatCurrency(transaction.amount)}
        </span>
      </td>
      <td className="text-right">
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatCurrency(transaction.running_balance || transaction.balance_after)}</span>
      </td>
    </tr>
  );
}

export default function CashBankPage() {
  const { hasPermission } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ date_from: '', date_to: '', type: '', account_id: '' });
  const [selectedAccount, setSelectedAccount] = useState(null);

  // Modals
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionType, setTransactionType] = useState('in'); // 'in' or 'out'
  const [form, setForm] = useState({ account_id: '', amount: '', description: '', date: '', reference: '' });
  const [transferForm, setTransferForm] = useState({
    from_account_id: '',
    to_account_id: '',
    amount: '',
    description: '',
    date: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [pagination.page, filters, selectedAccount]);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/cash-bank', { params: { type: 'accounts' } });
      if (res.data.success) {
        setAccounts(res.data.data?.accounts || res.data.data || []);
      }
    } catch {
      // Fallback: try without type param
      try {
        const res2 = await api.get('/cash-bank');
        if (res2.data.success) {
          const data = res2.data.data;
          if (Array.isArray(data)) {
            setAccounts(data.filter((a) => a.type === 'cash' || a.type === 'bank'));
          } else {
            setAccounts(data?.accounts || []);
          }
        }
      } catch (err2) {
        handleApiError(err2);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    setTxLoading(true);
    try {
      const params = queryParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
        account_id: selectedAccount?.id || '',
        type: filters.type,
      });
      const res = await api.get('/cash-bank/transactions', { params });
      if (res.data.success) {
        setTransactions(res.data.data?.transactions || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setTxLoading(false);
    }
  };

  const handleCashIn = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/cash-bank/transactions', { ...form, type: 'in' });
      if (res.data.success) {
        toast.success('Kas masuk berhasil dicatat');
        setShowTransactionModal(false);
        resetForm();
        fetchAccounts();
        fetchTransactions();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleCashOut = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/cash-bank/transactions', { ...form, type: 'out' });
      if (res.data.success) {
        toast.success('Kas keluar berhasil dicatat');
        setShowTransactionModal(false);
        resetForm();
        fetchAccounts();
        fetchTransactions();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/cash-bank/transfer', transferForm);
      if (res.data.success) {
        toast.success('Transfer berhasil');
        setShowTransferModal(false);
        resetTransferForm();
        fetchAccounts();
        fetchTransactions();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setForm({ account_id: '', amount: '', description: '', date: '', reference: '' });
  };

  const resetTransferForm = () => {
    setTransferForm({ from_account_id: '', to_account_id: '', amount: '', description: '', date: '' });
  };

  const openCashInModal = () => {
    setTransactionType('in');
    resetForm();
    if (selectedAccount) setForm((prev) => ({ ...prev, account_id: selectedAccount.id }));
    setShowTransactionModal(true);
  };

  const openCashOutModal = () => {
    setTransactionType('out');
    resetForm();
    if (selectedAccount) setForm((prev) => ({ ...prev, account_id: selectedAccount.id }));
    setShowTransactionModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Kas & Bank</h1>
          <p className="page-subtitle">Kelola rekening kas dan bank beserta transaksinya</p>
        </div>
        <div className="flex gap-2">
          {hasPermission('create_cash_bank') && (
            <>
              <button onClick={openCashInModal} className="btn-success btn-sm">
                <ArrowDownLeft className="h-4 w-4 mr-1" /> Kas Masuk
              </button>
              <button onClick={openCashOutModal} className="btn-danger btn-sm">
                <ArrowUpRight className="h-4 w-4 mr-1" /> Kas Keluar
              </button>
              <button onClick={() => { resetTransferForm(); setShowTransferModal(true); }} className="btn-primary btn-sm">
                <ArrowLeftRight className="h-4 w-4 mr-1" /> Transfer
              </button>
            </>
          )}
        </div>
      </div>

      {/* Account Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="stat-card">
              <div className="skeleton h-12 w-12 rounded-xl" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-4 w-32" />
                <div className="skeleton h-6 w-48" />
                <div className="skeleton h-3 w-24" />
              </div>
            </div>
          ))}
        </div>
      ) : accounts.length === 0 ? (
        <div className="card p-12 text-center">
          <Wallet className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Rekening</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada rekening kas/bank yang terdaftar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts.map((acc) => (
            <AccountCard
              key={acc.id}
              account={acc}
              onClick={(a) => {
                setSelectedAccount(selectedAccount?.id === a.id ? null : a);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
            />
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <select
                className="input"
                value={selectedAccount?.id || ''}
                onChange={(e) => {
                  const acc = accounts.find((a) => a.id == e.target.value);
                  setSelectedAccount(acc || null);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
              >
                <option value="">Semua Rekening</option>
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>{acc.name}</option>
                ))}
              </select>
            </div>
            <div>
              <select className="input" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
                <option value="">Semua Tipe</option>
                <option value="in">Kas Masuk</option>
                <option value="out">Kas Keluar</option>
                <option value="transfer_in">Transfer Masuk</option>
                <option value="transfer_out">Transfer Keluar</option>
              </select>
            </div>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                className="input pl-9"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                placeholder="Dari tanggal"
              />
            </div>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                className="input pl-9"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                placeholder="Sampai tanggal"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setPagination((prev) => ({ ...prev, page: 1 })); fetchTransactions(); }} className="btn-primary btn-sm flex-1">
                <Search className="h-4 w-4 mr-1" /> Filter
              </button>
              <button
                onClick={() => { setFilters({ date_from: '', date_to: '', type: '', account_id: '' }); setSelectedAccount(null); setPagination((prev) => ({ ...prev, page: 1 })); }}
                className="btn-secondary btn-sm"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Riwayat Transaksi {selectedAccount ? `- ${selectedAccount.name}` : ''}
          </h3>
        </div>
        {txLoading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-32" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-36" />
                  <div className="skeleton h-4 w-48" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-32 ml-auto" />
                </div>
              ))}
            </div>
          </div>
        ) : transactions.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <Banknote className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Transaksi</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada transaksi kas/bank yang tercatat.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Tanggal</th>
                    <th>Tipe</th>
                    <th>Rekening</th>
                    <th>Deskripsi</th>
                    <th>Referensi</th>
                    <th className="text-right">Jumlah</th>
                    <th className="text-right">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <TransactionRow key={tx.id} transaction={tx} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page <= 1}
                    className="btn btn-sm btn-outline"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">
                    Halaman {pagination.page} / {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                    disabled={pagination.page >= pagination.totalPages}
                    className="btn btn-sm btn-outline"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Cash In / Cash Out Modal */}
      {showTransactionModal && (
        <div className="modal-overlay" onClick={() => setShowTransactionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">
                {transactionType === 'in' ? 'Kas Masuk' : 'Kas Keluar'}
              </h3>
              <button onClick={() => setShowTransactionModal(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={transactionType === 'in' ? handleCashIn : handleCashOut} className="p-6 space-y-4">
              <div>
                <label className="label">Rekening <span className="text-danger-600">*</span></label>
                <select
                  className="input"
                  value={form.account_id}
                  onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                  required
                >
                  <option value="">Pilih Rekening</option>
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>{acc.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Jumlah <span className="text-danger-600">*</span></label>
                <input
                  type="number"
                  className="input"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                  min="1"
                  placeholder="Masukkan jumlah"
                />
              </div>
              <div>
                <label className="label">Tanggal <span className="text-danger-600">*</span></label>
                <input
                  type="date"
                  className="input"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="label">Deskripsi</label>
                <textarea
                  className="input min-h-[80px]"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Deskripsi transaksi"
                  rows={3}
                />
              </div>
              <div>
                <label className="label">Referensi</label>
                <input
                  type="text"
                  className="input"
                  value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  placeholder="No. referensi / bukti"
                />
              </div>
              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => setShowTransactionModal(false)} className="btn-secondary flex-1">
                  Batal
                </button>
                <button type="submit" disabled={saving} className={`${transactionType === 'in' ? 'btn-success' : 'btn-danger'} flex-1`}>
                  {saving ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && (
        <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Transfer Antar Rekening</h3>
              <button onClick={() => setShowTransferModal(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleTransfer} className="p-6 space-y-4">
              <div>
                <label className="label">Rekening Sumber <span className="text-danger-600">*</span></label>
                <select
                  className="input"
                  value={transferForm.from_account_id}
                  onChange={(e) => setTransferForm({ ...transferForm, from_account_id: e.target.value })}
                  required
                >
                  <option value="">Pilih Rekening Sumber</option>
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>{acc.name} ({formatCurrency(acc.balance)})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Rekening Tujuan <span className="text-danger-600">*</span></label>
                <select
                  className="input"
                  value={transferForm.to_account_id}
                  onChange={(e) => setTransferForm({ ...transferForm, to_account_id: e.target.value })}
                  required
                >
                  <option value="">Pilih Rekening Tujuan</option>
                  {accounts
                    .filter((acc) => acc.id != transferForm.from_account_id)
                    .map((acc) => (
                      <option key={acc.id} value={acc.id}>{acc.name} ({formatCurrency(acc.balance)})</option>
                    ))}
                </select>
              </div>
              <div>
                <label className="label">Jumlah <span className="text-danger-600">*</span></label>
                <input
                  type="number"
                  className="input"
                  value={transferForm.amount}
                  onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
                  required
                  min="1"
                  placeholder="Masukkan jumlah transfer"
                />
              </div>
              <div>
                <label className="label">Tanggal <span className="text-danger-600">*</span></label>
                <input
                  type="date"
                  className="input"
                  value={transferForm.date}
                  onChange={(e) => setTransferForm({ ...transferForm, date: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="label">Deskripsi</label>
                <textarea
                  className="input min-h-[80px]"
                  value={transferForm.description}
                  onChange={(e) => setTransferForm({ ...transferForm, description: e.target.value })}
                  placeholder="Deskripsi transfer"
                  rows={3}
                />
              </div>
              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => setShowTransferModal(false)} className="btn-secondary flex-1">
                  Batal
                </button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">
                  {saving ? 'Memproses...' : 'Transfer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
